/**
 * Customer display — what to show, shared by web/Electron and mobile.
 *
 * Pure derivation over a sale document: no redux, no platform APIs, no React.
 * A single file rather than a shared/desktop/native folder because none of it
 * is platform-specific — see CLAUDE.md.
 *
 * The UI lives in @rista/ui/components/CustomerDisplay and renders these; the
 * transport that carries a sale to a second monitor is desktop shell glue and
 * deliberately stays in apps/web.
 *
 * ## Media URLs live in `media/imageUrls` now
 *
 * The "uncropped `original` first, sized derivatives as fallbacks" list was written here and
 * moved out on 2026-09-09, when the KIOSK turned out to need the same ordering for its item
 * artwork. That file's header carries the reasoning (sharp center-crops every sized
 * derivative, so `large` is 110x110 and cropped); this one just consumes it.
 *
 * `getMediaKind` is IMPORTED as well as re-exported: `resolveMediaItem` calls it, and a bare
 * `export … from` creates no local binding — the `ReferenceError` CLAUDE.md warns about when
 * an uplifted symbol is re-exported and then used. It keeps its old import path because this
 * module's consumers already take it from here.
 */
import { buildImageUrls, getMediaKind } from "../media/imageUrls";
import type { MoneyFormat } from "../utils/currency/format";

export { getMediaKind };

/** What the desktop shell pushes to the customer-display window. */
export interface CustomerCartPayload {
  /** SaleDataType from the sale store. Untyped so core stays free of app models. */
  sale?: any;
  /** Resolved currency SYMBOL (not code) — apps call their own currency() helper. */
  currency?: string;
  /**
   * Digits after the point, from `amountDecimals()`. Resolved by the SENDER like the symbol,
   * because this payload crosses a window boundary and the display has no store to read.
   * Absent is safe: `formatAmount` sends everything through `amountDecimalsFor`.
   */
  decimals?: number;
  /**
   * Grouping, from `amountLocale()`. Resolved by the SENDER for the same reason the
   * symbol and the precision are — this payload crosses a window boundary and the display has no
   * store. Absent is safe: the view falls back to `DEFAULT_AMOUNT_LOCALE`, which is what a
   * sender older than 2026-09-19 effectively sent.
   */
  grouping?: string | false;
  campaignConfig?: any;
  /**
   * `${baseURL}/${businessData.staticFolder}` — resolved by the caller (each
   * app reads its own env base URL + redux businessData) since this module
   * stays free of redux/env access. Used to turn a campaign media item's
   * `mid` into a playable images/... or video/... URL, see getSortedMedia.
   */
  staticMediaBaseUrl?: string;
  /** The business's logo — see getBusinessLogoUrl. Undefined when it has none. */
  logoUrl?: string;
  /** Per-item image + veg/non-veg tag icon, keyed by itemId — see buildItemDisplayInfoMap. */
  itemDisplayInfo?: Record<string, ItemDisplayInfo>;
  paymentDetails?: any;
  isPaymentInProcess?: boolean;
  canCustomerEnterNumber?: boolean;
  fetchCustomerNumber?: any;
  /**
   * `Date.now()` of the last sale closed on this terminal — see
   * customerDisplayStore.saleClosedAt. A TIMESTAMP rather than a boolean because the display
   * has to hold the success screen for a fixed window (SUCCESS_VIEW_HOLD_MS) that a display
   * mounting late must not restart from zero.
   */
  saleClosedAt?: number;
}

/**
 * How long "Order successfully placed" stays up after a sale closes.
 *
 * The closed sale is never what the display is holding by then: checkout success clears the
 * cart (resetSaleData) within the same tick, and an empty cart is the idle screen — so without
 * this hold the success view was replaced before a customer could read it, if it rendered at all.
 */
export const SUCCESS_VIEW_HOLD_MS = 5000;

/**
 * Case-insensitive on purpose: the sale document carries SALE_STATUS_TYPES.CLOSED — "Closed",
 * capitalised — and comparing against a bare "closed" silently never matched. Same reason
 * SPENT_PAYMENT_STATUSES lowercases below.
 */
export function isSaleClosed(sale: any): boolean {
  return `${sale?.saleStatus || ""}`.toLowerCase() === "closed";
}

/**
 * Items a customer should see. On-hold items are punched but not part of the
 * order yet, so showing them would quote a total the customer is not being
 * charged.
 */
export function getDisplayItems(sale: any): any[] {
  return (sale?.saleItems || []).filter((item: any) => !item?.onHold);
}

const CDN_ORACLE_URL = "https://cdn.dotpe.in/oracle";

/** Shown in place of an item's own image when it has none. */
export const DEFAULT_ITEM_IMAGE_URL = `${CDN_ORACLE_URL}/cutlery.svg`;

const TAG_TYPE_ICON_URL: Record<string, string> = {
  veg: `${CDN_ORACLE_URL}/veg.svg`,
  nveg: `${CDN_ORACLE_URL}/nveg.svg`,
  egg: `${CDN_ORACLE_URL}/egg.svg`,
};

/** Shown next to an item's note/instruction (e.g. "Less Spicy"). */
export const ITEM_NOTE_ICON_URL = `${CDN_ORACLE_URL}/note_edit.svg`;

export type ItemTagType = "veg" | "nveg" | "egg" | null;

export interface ItemDisplayInfo {
  imageUrl: string;
  tagIconUrl?: string;
}

/**
 * Veg / egg / non-veg, from an item's catalogue tag ids plus the business's
 * tag-priority order (non-veg first, then egg, then veg). Mirrors
 * apps/web's provideItemTag so the same item resolves to the same tag here.
 */
export function resolveItemTagType(
  tagIds: string[] | undefined,
  tagsPriorityArr: string[] | undefined,
): ItemTagType {
  if (!tagsPriorityArr?.length || !tagIds?.length) return null;
  if (tagIds.includes(tagsPriorityArr[0])) return "nveg";
  if (tagIds.includes(tagsPriorityArr[1])) return "egg";
  if (tagIds.includes(tagsPriorityArr[2])) return "veg";
  return null;
}

/**
 * `${staticMediaBaseUrl}images/${imageDataId}/large`, the same catalogue
 * image URL pattern getImageUrl(id, "large") builds — falls back to a
 * cutlery placeholder when the item has no image.
 */
export function getItemImageUrl(
  imageDataId: string | undefined,
  staticMediaBaseUrl: string | undefined,
): string {
  if (!imageDataId || !staticMediaBaseUrl) return DEFAULT_ITEM_IMAGE_URL;
  return `${staticMediaBaseUrl}images/${imageDataId}/large`;
}

/**
 * The business's own logo, `logo/${imageDataId}/small` — its own CDN prefix,
 * NOT the `images/` one item pictures use. The id is the BUSINESS's
 * `imageDataId` (businessStore.businessData), not an item's. Undefined when
 * the business has no logo, which callers render as "no logo" rather than a
 * placeholder.
 */
export function getBusinessLogoUrl(
  imageDataId: string | undefined,
  staticMediaBaseUrl: string | undefined,
): string | undefined {
  if (!imageDataId || !staticMediaBaseUrl) return undefined;
  return `${staticMediaBaseUrl}logo/${imageDataId}/small`;
}

/**
 * Per-item image + veg/non-veg tag icon, keyed by itemId. Built by the
 * caller from the catalogue map (`itemsStore.itemsData`) and the business's
 * tag priority, since this module stays free of redux — mirrors how
 * CustomerCartPayload.staticMediaBaseUrl is resolved by each app.
 */
export function buildItemDisplayInfoMap(
  items: any[],
  itemsData: Record<string, any> | undefined,
  staticMediaBaseUrl: string | undefined,
  tagsPriorityArr: string[] | undefined,
): Record<string, ItemDisplayInfo> {
  const map: Record<string, ItemDisplayInfo> = {};
  (items || []).forEach((item: any) => {
    const itemId = item?.itemId;
    if (!itemId || map[itemId]) return;
    const info = itemsData?.[itemId]?.info;
    const tagType = resolveItemTagType(info?.tagIds, tagsPriorityArr);
    map[itemId] = {
      imageUrl: getItemImageUrl(info?.imageDataId, staticMediaBaseUrl),
      tagIconUrl: tagType ? TAG_TYPE_ICON_URL[tagType] : undefined,
    };
  });
  return map;
}

/** Item name with its variant values appended, e.g. "Latte - Large, Oat". */
export function getItemName(item: any): string {
  const base = item?.punchedName || item?.longName || item?.shortName || "";
  const variants = (item?.variantValues || [])
    .map((v: any) => v?.value)
    .filter(Boolean);
  return variants.length ? `${base} - ${variants.join(", ")}` : base;
}

/**
 * "₹1234.50" — re-exported from `utils/currency`, which owns every money string in the product.
 *
 * 🔴 It was a SECOND definition of this name, with a different argument order and a hardcoded two
 * decimals. The shared one takes `(value, symbol, decimals, grouping)`; the changed arity is deliberate —
 * it makes TypeScript name every stale call site instead of letting one quietly keep printing two
 * digits for a merchant configured for three. `utils/currency/format` has zero imports, so
 * this module stays store-free.
 */
export { formatMoney } from "../utils/currency/format";

/** Final amount payable — rounded if the business opted into rounding. */
export function getBillTotal(sale: any): number {
  return sale?.billRoundedAmount ?? sale?.billTotal ?? 0;
}

export interface BillBreakdownRow {
  label: string;
  amount: number;
}

/**
 * Summary-panel rows: item total, then each named charge (e.g. "Packaging
 * Charges"), discount, each bill-level tax (e.g. "CGST 2.5% on ₹399"), and
 * round-off — zero-valued rows dropped so the customer sees a short summary
 * rather than a column of "0.00" rows. Order matches the printed receipt.
 *
 * discountTotal is stored negative, so it already reads as a deduction.
 */
export function getOrderSummaryRows(sale: any): BillBreakdownRow[] {
  const rows: BillBreakdownRow[] = [
    { label: "Item Total", amount: Number(sale?.billSubTotal || 0) },
  ];

  (sale?.chargeList || []).forEach((charge: any) => {
    const amount = Number(charge?.amount || 0);
    if (amount !== 0) rows.push({ label: charge?.name || "Charges", amount });
  });

  const discountTotal = Number(sale?.billDiscountTotal || 0);
  if (discountTotal !== 0) rows.push({ label: "Discount", amount: discountTotal });

  (sale?.billTaxTotal) && rows.push({ label: "Taxes", amount: sale.billTaxTotal });

  const roundOff = Number(sale?.roundOffAmount || 0);
  if (roundOff !== 0) rows.push({ label: "Round Off", amount: roundOff });

  return rows;
}

export interface CustomerDisplaySummary {
  isWalkIn: boolean;
  /** Display-ready: already elided at CUSTOMER_NAME_MAX_LENGTH. */
  name?: string;
  maskedPhone?: string;
  channelName?: string;
}

/** Last 4 digits visible, e.g. "9876543210" -> "xxxxx-x3210". */
function maskCustomerPhone(phone: string | undefined): string | undefined {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return undefined;
  return digits.length <= 4 ? digits : `xxxxx-x${digits.slice(-4)}`;
}

/** The header chip sits beside the title, so a long name is elided rather than pushing it. */
const CUSTOMER_NAME_MAX_LENGTH = 10;

/** e.g. "Bartholomew" -> "Bartholome...". Trailing space is dropped so the cut doesn't read as a gap. */
function truncateCustomerName(name: string): string {
  if (name.length <= CUSTOMER_NAME_MAX_LENGTH) return name;
  return `${name.slice(0, CUSTOMER_NAME_MAX_LENGTH).trimEnd()}...`;
}

/**
 * Walk-in vs named customer, for the order-section header chip.
 * `sale.customerInfo` is untyped (see CustomerCartPayload) — its runtime
 * shape is `{ contactInfo: { name: { firstName, lastName }, phoneNumber } }`.
 */
export function getCustomerDisplaySummary(sale: any): CustomerDisplaySummary {
  const contactInfo = sale?.customerInfo?.contactInfo;
  const name = contactInfo?.name?.firstName || "";
  const phone = contactInfo?.phoneNumber || contactInfo?.phone || "";
  // saleHelpers stamps the selected channel onto every sale it creates.
  const channelName = sale?.channelName || undefined;

  if (!name && !phone) {
    return { isWalkIn: true, channelName };
  }

  return {
    isWalkIn: false,
    name: name ? truncateCustomerName(name) : undefined,
    maskedPhone: maskCustomerPhone(phone),
    channelName,
  };
}


export function parseMediaSplitPercentage(
  rawSplit: unknown,
  fallbackValue = 50,
): number {
  const valueToParse = rawSplit ?? fallbackValue;

  if (
    typeof valueToParse === "string" &&
    valueToParse.includes("/")
  ) {
    const parsedMedia = parseInt(
      valueToParse.split("/")[0],
      10,
    );

    if (!Number.isNaN(parsedMedia)) {
      return Math.min(
        Math.max(parsedMedia, 0),
        100,
      );
    }
  }

  const numericSplit =
    typeof valueToParse === "string"
      ? parseInt(valueToParse, 10)
      : Number(valueToParse);

  return Math.min(
    Math.max(
      Number.isNaN(numericSplit)
        ? fallbackValue
        : numericSplit,
      0,
    ),
    100,
  );
}

export interface ResolvedMediaItem {
  id: string;
  order: number;
  type: "image" | "video";
  url: string;
  /** Video only — a still to show while the clip loads. */
  posterUrl?: string;
  /**
   * Progressively smaller URLs to try, in order, when `url` fails to load —
   * see IMAGE_SIZE_FALLBACKS. Empty for videos.
   */
  fallbackUrls: string[];
}

/** Every URL to try for an item, best first — what MediaSlide steps through on load errors. */
export function getMediaUrlCandidates(item: ResolvedMediaItem): string[] {
  return [item.url, ...(item.fallbackUrls || [])];
}

/**
 * Turns one campaign media entry (`{ name, mid }`) into a playable URL.
 * `staticMediaBaseUrl` is `${baseURL}/${businessData.staticFolder}`, matching
 * the images/${mid}/${size} pattern already used for catalogue images.
 *
 * Images resolve to the uncropped `original` first — a campaign slide fills a
 * whole customer display, so the cropped catalogue sizes are fallbacks only.
 */
export function resolveMediaItem(
  item: any,
  order: number,
  staticMediaBaseUrl: string | undefined,
): ResolvedMediaItem | undefined {
  const mid = item?.mid;

  if (!mid || !staticMediaBaseUrl) {
    return undefined;
  }

  const type = getMediaKind(item?.name);

  if (type === "video") {
    return {
      id: mid,
      order,
      type,
      url: `${staticMediaBaseUrl}videos/${mid}/compressed.mp4`,
      posterUrl: `${staticMediaBaseUrl}videos/${mid}/thumbnail.jpg`,
      fallbackUrls: [],
    };
  }

  const [url, ...fallbackUrls] = buildImageUrls(mid, staticMediaBaseUrl, item?.name);

  return {
    id: mid,
    order,
    type,
    url,
    fallbackUrls,
  };
}

export function getSortedMedia(
  display: any,
  staticMediaBaseUrl: string | undefined,
): ResolvedMediaItem[] {
  const media = Array.isArray(display?.media)
    ? display.media
    : [];

  return media
    .map((item: any, index: number) =>
      resolveMediaItem(item, Number(item?.order ?? index), staticMediaBaseUrl),
    )
    .filter((item: ResolvedMediaItem | undefined): item is ResolvedMediaItem => item != null)
    .sort((a: ResolvedMediaItem, b: ResolvedMediaItem) => a.order - b.order);
}


/** How long a payment QR is shown as valid on the customer display. */
export const QR_PAYMENT_COUNTDOWN_SECONDS = 180;

/** Seconds as "m:ss" — 180 -> "3:00", 9 -> "0:09". Clamped at zero. */
export function formatCountdown(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export type PaymentDetail = {
  paymentId: string;
  type: string;
  mode: string;
  isCash?: boolean;
  amount?: number;
  originalAmount?: number;
  requestAmount?: number;
  pid?: string;
  img?: string;
  qrData?: string;
  paymentStatus?: string;
};

/**
 * Payment states a QR must NOT be shown for. Compared lowercased because the
 * app's own status constants are spelled inconsistently — "Cancelled",
 * "Failed", "DECLINED", "COMPLETED".
 */
const SPENT_PAYMENT_STATUSES = new Set([
  "cancelled",
  "failed",
  "error",
  "declined",
  "refunded",
  "completed",
]);

/** True while a payment leg is still waiting on the customer. */
export function isPaymentAwaitingCustomer(payment: any): boolean {
  return !SPENT_PAYMENT_STATUSES.has(`${payment?.paymentStatus || ""}`.toLowerCase());
}

/**
 * The QR the customer should be scanning right now.
 *
 * Cancelling a DQR does not remove its leg from the payments map — the
 * regenerated one is added alongside it — so this takes the LAST live match
 * rather than the first. Picking the first showed the cancelled code's QR for
 * the rest of the checkout.
 */
export function getActiveQrPayment(
  paymentDetails: Map<string, PaymentDetail> | undefined,
): PaymentDetail | undefined {
  if (!paymentDetails) return undefined;

  let activePayment: PaymentDetail | undefined;

  paymentDetails.forEach((payment) => {
    if (payment?.qrData && isPaymentAwaitingCustomer(payment)) {
      activePayment = payment;
    }
  });

  return activePayment;
}

export type MediaSectionProps = {
  media: ResolvedMediaItem[];
  dwellSeconds: number;
};

export type OrderSectionProps = {
  sale: any;
  items: any[];
  /**
   * A bound formatter — `money(1234.5)` -> `"₹1234.50"`. The section takes the whole function
   * rather than a symbol, so it never has to know the currency or the precision; the display's
   * root builds it once from the payload's `currency` + `decimals`.
   */
  money: MoneyFormat;
  itemDisplayInfo?: Record<string, ItemDisplayInfo>;
};

export type PaymentSectionProps = {
  sale: any;
  /**
   * A bound formatter — `money(1234.5)` -> `"₹1234.50"`. The section takes the whole function
   * rather than a symbol, so it never has to know the currency or the precision; the display's
   * root builds it once from the payload's `currency` + `decimals`.
   */
  money: MoneyFormat;
  paymentDetails?: Map<string, PaymentDetail>;
  isPaymentInProcess: boolean;
  /**
   * True while the post-payment success screen is being held open — see
   * SUCCESS_VIEW_HOLD_MS. Separate from `sale`, which by then is the fresh empty cart.
   */
  isOrderSuccess?: boolean;
};

export const DISPLAY_SCREEN_TYPE = {
  IDLE: "idleDisplay",
  ORDER: "orderDisplay",
} as const;